import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { QUERY_SERVICE } from '../app.tokens';
import { IQueryService } from '../interfaces/query.service';
import { AuthResult } from "../models/users/auth-result.model";
import { LoggerService } from './logger.service';


declare var pendo: any;


@Injectable({ providedIn: 'root' })
export class UsersService {
    private static readonly storageKey: string = 'currentUser';
    private currentUserSubject: BehaviorSubject<AuthResult>;

    currentUser$: Observable<AuthResult>;

    get currentUser(): AuthResult {
        return this.currentUserSubject.value;
    }

    constructor(
        @Inject(QUERY_SERVICE) private queryService: IQueryService,
        private loggerService: LoggerService) {
        const authResult = <AuthResult>JSON.parse(localStorage.getItem(UsersService.storageKey));

        if (authResult?.issued_date && typeof authResult.issued_date === 'string') {
            authResult.issued_date = new Date(Date.parse(authResult.issued_date));
        }

        if (authResult?.expiration_date && typeof authResult.expiration_date === 'string') {
            authResult.expiration_date = new Date(Date.parse(authResult.expiration_date));
        }

        this.currentUserSubject = new BehaviorSubject<AuthResult>(authResult);
        this.currentUser$ = this.currentUserSubject.asObservable();

        this.initializePendo(authResult);
    }

    login(authCode: string): Observable<AuthResult> {
        return this.queryService.login(authCode)
            .pipe(
                tap(res => {
                    localStorage.setItem(UsersService.storageKey, JSON.stringify(res));
                    this.currentUserSubject.next(res);
                }),
                tap(res => this.initializePendo(res)));
    }

    refreshToken(): Observable<AuthResult> {
        return this.queryService.refreshAccessToken(this.currentUser.refresh_token)
            .pipe(
                tap(res => {
                    res.refresh_token = this.currentUser.refresh_token;
                    localStorage.setItem(UsersService.storageKey, JSON.stringify(res));
                    this.currentUserSubject.next(res);
                }),
                tap(res => this.initializePendo(res)));
    }

    logout(): void {
        localStorage.removeItem(UsersService.storageKey);
        this.currentUserSubject.next(null);
    }

    private initializePendo(authResult: AuthResult): void {
        if (environment.production && authResult?.userId) {
            try {
                pendo.initialize({
                    visitor: {
                        id: authResult.userId
                    },
                    account: {
                        id: 'TaxonomyBuilder-Addin-Stage'
                    }
                });
            } catch (error) {
                this.loggerService.error(error);
            }
        }
    }
}